/* eslint-disable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument */
import { formatDateTimeInVenueTz } from './helpers/formatDateTime';
import { getVenues } from '@openapi/venues/venues';
import { getShows } from '@openapi/shows/shows';
import { getTicketOptions } from '@openapi/ticket-options/ticket-options';
import {
    getTicketSeats,
    getFreeTicketSeats,
    searchTicketSeatsById,
} from '@openapi/ticket-seats/ticket-seats';
import { getTicketTypes } from '@openapi/ticket-types/ticket-types';
import { getTimeslots } from '@openapi/timeslots/timeslots';

export const venuesAutocomplete = {};
venuesAutocomplete.init = async (ids) => {
    if (ids.length === 1 && ids[0] === '') {
        return [];
    }
    const { venues } = await getVenues({
        pageSize: ids.length,
        idList: ids,
    });
    return venues
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((e) => {
            return { id: e.id, name: e.name, site: e.site };
        });
};
venuesAutocomplete.search = async (search, pageSize) => {
    const { venues } = await getVenues({
        pageSize,
        search,
    });
    return venues
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((e) => {
            return { id: e.id, name: e.name, site: e.site };
        });
};

export const showsAutocomplete = {};
showsAutocomplete.init = async (ids) => {
    if (ids.length === 1 && ids[0] === '') {
        return [];
    }
    const { shows } = await getShows({
        pageSize: ids.length,
        idList: ids,
    });
    return shows
        .sort((a, b) => a.title.localeCompare(b.title))
        .map((e) => {
            return { id: e.id, name: e.title, description: e.description };
        });
};
showsAutocomplete.search = async (search, pageSize) => {
    const { shows } = await getShows({
        pageSize,
        search,
    });
    return shows
        .sort((a, b) => a.title.localeCompare(b.title))
        .map((e) => {
            return { id: e.id, name: e.title, description: e.description };
        });
};

export const ticketOptionsAutocomplete = {};
ticketOptionsAutocomplete.init = async (ids) => {
    if (ids.length === 0 || (ids.length === 1 && ids[0] === '')) {
        return [];
    }
    const { ticketOptions } = await getTicketOptions({
        pageSize: ids.length,
        idList: ids,
    });
    return ticketOptions
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((e) => {
            return { id: e.id, name: e.name };
        });
};
ticketOptionsAutocomplete.search = async (search, pageSize, { showId }) => {
    const { ticketOptions } = await getTicketOptions({
        pageSize,
        search,
        optionalId: showId,
    });
    return ticketOptions
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((e) => {
            return { id: e.id, name: e.name };
        });
};

export const ticketSeatsAutocomplete = {};
ticketSeatsAutocomplete.init = async (ids, { showId }) => {
    if (ids.length === 0 || (ids.length === 1 && ids[0] === '')) {
        return [];
    }
    const { ticketSeats } = await searchTicketSeatsById(showId, {
        idList: ids,
    });
    return ticketSeats
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((e) => {
            return { id: e.id, name: e.name };
        });
};
ticketSeatsAutocomplete.search = async (search, pageSize, { showId, isBookable }) => {
    const { ticketSeats } = await getTicketSeats(showId, {
        pageSize,
        search,
        isBookable,
    });
    return ticketSeats
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((e) => {
            return { id: e.id, name: e.name };
        });
};

export const freeTicketSeatsAutocomplete = {};
freeTicketSeatsAutocomplete.search = async (
    search,
    pageSize,
    { showId, timeslotId, ticketTypeId, bookingId, excludedSeatIds = [] },
) => {
    if (!showId) {
        return [];
    }

    const { ticketSeats } = await getFreeTicketSeats(showId, {
        pageSize,
        search,
        timeslotId,
        ...(ticketTypeId && { ticketTypeId }),
        bookingId,
    });
    const excludedSet = new Set(excludedSeatIds.map(Number));
    return ticketSeats
        .filter((s) => !excludedSet.has(s.id))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((e) => {
            return { id: e.id, name: e.name };
        });
};

export const ticketTypesAutocomplete = {};
ticketTypesAutocomplete.init = async (ids) => {
    if (ids.length === 0 || (ids.length === 1 && ids[0] === '')) {
        return [];
    }
    const { ticketTypes } = await getTicketTypes({
        pageSize: ids.length,
        idList: ids,
        onlyShowInList: true,
    });
    return ticketTypes
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((e) => {
            return {
                id: e.id,
                name: e.name,
                priceMatrix: e.priceMatrix,
                automaticSeating: e.automaticSeating,
                canBuyAlone: e.canBuyAlone,
            };
        });
};
ticketTypesAutocomplete.search = async (search, pageSize, { showId }) => {
    const { ticketTypes } = await getTicketTypes({
        pageSize,
        search,
        optionalId: showId,
        onlyShowInList: true,
    });
    return ticketTypes
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((e) => {
            return {
                id: e.id,
                name: e.name,
                priceMatrix: e.priceMatrix,
                automaticSeating: e.automaticSeating,
                canBuyAlone: e.canBuyAlone,
            };
        });
};

export const timeslotsAutocomplete = {};
timeslotsAutocomplete.init = async (ids) => {
    if (ids.length === 0 || (ids.length === 1 && ids[0] === '')) {
        return [];
    }
    const { timeslots } = await getTimeslots({
        pageSize: ids.length,
        idList: ids,
    });
    return timeslots
        .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
        .map((e) => {
            return {
                id: e.id,
                name: formatDateTimeInVenueTz(e.startTime),
                showTitle: e.showTitle,
                showVenueId: e.showVenueId,
                freeTicketSeatNumber: e.freeTicketSeatNumber,
            };
        });
};
timeslotsAutocomplete.search = async (search, pageSize, { showId }) => {
    const { timeslots } = await getTimeslots({
        pageSize,
        search,
        optionalId: showId,
    });
    return timeslots
        .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
        .map((e) => {
            return {
                id: e.id,
                name: formatDateTimeInVenueTz(e.startTime),
                showTitle: e.showTitle,
                showVenueId: e.showVenueId,
                freeTicketSeatNumber: e.freeTicketSeatNumber,
            };
        });
};
